import React, { ReactNode, useContext, useState } from "react";
import {
  BotFormType,
  HedgingMode,
  SummaryData,
} from "@shared/features/bots/BotForm/common/types";
import OrdersSummary from "features/bots/BotForm/Summaries/OrdersSummary";
import HedgingGridsSummary from "features/bots/BotForm/Summaries/HedgingGridsSummary";
import Drawer from "common/Drawers/Drawer";
import Tabs from "common/Tabs/Tabs";
import { BotData, BotType, LanguageNameSpaces } from "@shared/types";
import styled, { css } from "styled-components";
import { panelTabs } from "features/bots/BotForm/NewLook/styles";
import BotFormChart from "features/bots/BotForm/BotFormChart";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { isSymbol } from "@shared/types/utils";
import DefaultGroupConfigurationMessage from "features/bots/BotForm/common/DefaultGroupConfigurationMessage";
import SignalsBotSummaryTabs from "features/bots/BotForm/Summaries/SignalsBotSummaryTabs";
import BacktestingHistory from "features/bots/BotForm/Backtesting/BacktestingHistory";
import FormPublicBots from "features/bots/PublicBots/FormPublicBots.tsx";
import useMeasure from "utils/hooks/useMeasure.ts";
import BotsSummary from "features/bots/BotForm/Summaries/BotsSummary.tsx";
import BotConfigurationModal from "features/bots/BotForm/common/BotConfigurationModal.tsx";
import Text from "common/Typography/Text";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface SummaryDrawerProps {
  isOpen: boolean;
  entriesSummaryData: SummaryData;
  takeProfitsSummaryData: SummaryData;
  botSummary: ReactNode;
  closeDrawer: () => void;
  isChart?: boolean;
}

const SummaryDrawer = ({
  isOpen,
  entriesSummaryData,
  takeProfitsSummaryData,
  botSummary,
  closeDrawer,
  isChart,
}: SummaryDrawerProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    botType,
    subGroup,
    account,
    isChannelConfiguration,
    showBacktesting,
    showPublicBots,
    botFormType,
    hedgingMode,
  } = useContext(BotFormContext)!;
  const [tab, setTab] = useState<SummaryTabs>(SummaryTabs.ENTRIES);
  const isSignalsBot = botType === BotType.SIGNAL;
  const isGridBot = botType === BotType.GRID;
  const isRangeHedgingBot =
    botType === BotType.HEDGING && hedgingMode === HedgingMode.RANGE;
  const [summaryMeasureRef, { height: summaryHeight }] =
    useMeasure<HTMLDivElement>();
  const [measureRef, { height }] = useMeasure<HTMLDivElement>();
  const [configurationModalBot, setConfigurationModalBot] = useState<BotData>();
  const isBulkActions =
    botFormType === BotFormType.BULK_CREATE ||
    botFormType === BotFormType.BULK_EDIT;

  const getSummaryContent = () => {
    if (isSignalsBot) {
      return (
        <div>
          <DefaultGroupConfigurationMessage
            style={{ margin: "20px 16px 16px" }}
            subGroupId={subGroup?.id}
            exchange={
              account && !isSymbol(account) ? account.exchange : undefined
            }
          />
          {!isChannelConfiguration && (
            <SignalsBotSummaryTabs height={height - summaryHeight} />
          )}
        </div>
      );
    }
    if (isGridBot || isRangeHedgingBot) {
      return (
        <OrdersWrapper>
          <SummaryTitle as="div">
            {isRangeHedgingBot
              ? t("summary.hedging_summary")
              : t("summary.grids_summary")}
          </SummaryTitle>
          {isRangeHedgingBot ? (
            <HedgingGridsSummary orders={entriesSummaryData.orders} />
          ) : (
            <OrdersSummary
              {...entriesSummaryData}
              key={entriesSummaryData.key}
            />
          )}
        </OrdersWrapper>
      );
    }
    return (
      <StyledTabs
        activeKey={tab}
        onChange={(key) => setTab(key as SummaryTabs)}
        $isSmall={true}
        items={tabsItems}
      />
    );
  };

  const tabsItems = [
    {
      key: SummaryTabs.ENTRIES,
      label: SummaryTabs.ENTRIES,
      children: (
        <OrdersWrapper>
          <OrdersSummary {...entriesSummaryData} key={entriesSummaryData.key} />
        </OrdersWrapper>
      ),
    },
    {
      key: SummaryTabs.TAKE_PROFITS,
      label: SummaryTabs.TAKE_PROFITS,
      children: (
        <OrdersWrapper>
          <OrdersSummary
            {...takeProfitsSummaryData}
            key={takeProfitsSummaryData.key}
          />
        </OrdersWrapper>
      ),
    },
  ];

  if (showBacktesting) {
    tabsItems.push({
      key: SummaryTabs.BACKTESTING_HISTORY,
      label: SummaryTabs.BACKTESTING_HISTORY,
      children: <BacktestingHistory />,
    });
  }

  if (showPublicBots) {
    tabsItems.push({
      key: SummaryTabs.PUBLIC_BOTS,
      label: SummaryTabs.PUBLIC_BOTS,
      children: <FormPublicBots />,
    });
  }

  const getContent = () => {
    if (isChart)
      return (
        <ChartWrapper>
          <BotFormChart />
        </ChartWrapper>
      );
    else if (isBulkActions) {
      return (
        <>
          <BulkActionsSummariesWrapper>
            <BulkSummaryWrapper>
              <BotsSummary
                isValid={false}
                onBotClick={setConfigurationModalBot}
                key="validBots"
              />
            </BulkSummaryWrapper>
            <BotsSummary
              isValid={true}
              onBotClick={setConfigurationModalBot}
              key="invalidBots"
            />
          </BulkActionsSummariesWrapper>
          <SummaryWrapper ref={summaryMeasureRef}>{botSummary}</SummaryWrapper>
        </>
      );
    }
    return (
      <>
        <TabsContentWrapper ref={measureRef}>
          {getSummaryContent()}
        </TabsContentWrapper>
        <SummaryWrapper ref={summaryMeasureRef}>{botSummary}</SummaryWrapper>
      </>
    );
  };

  return (
    <StyledDrawer closable={true} onClose={closeDrawer} open={isOpen}>
      {getContent()}
      <BotConfigurationModal
        bot={configurationModalBot}
        open={!!configurationModalBot}
        closeModal={() => setConfigurationModalBot(undefined)}
        isBulkEdit={isBulkActions}
      />
    </StyledDrawer>
  );
};

export default SummaryDrawer;

enum SummaryTabs {
  ENTRIES = "Entries",
  TAKE_PROFITS = "Take-Profits",
  BACKTESTING_HISTORY = "Backtesting History",
  PUBLIC_BOTS = "Public Bots",
}

const StyledTabs = styled(Tabs)`
  &.ant-tabs {
    height: 100%;
  }
  .ant-tabs-content,
  .ant-tabs-tabpane {
    height: 100%;
  }
  &.ant-tabs-top > .ant-tabs-nav {
    padding: 0 12px;
    margin-bottom: 0;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      ${panelTabs};
      &.ant-tabs-top > .ant-tabs-nav {
        padding: 0;
      }
      &.ant-tabs > .ant-tabs-nav .ant-tabs-nav-wrap {
        padding: 0 16px;
      }
      &.ant-tabs .ant-tabs-tab {
        font-size: 13.5px;
      }
      &.ant-tabs .ant-tabs-tab + .ant-tabs-tab {
        margin-left: 18px;
      }
    `};
`;
const BulkActionsSummariesWrapper = styled.div`
  flex: 1;
  padding: 8px 12px 20px;
  overflow: auto;
`;
const BulkSummaryWrapper = styled.div`
  border-bottom: ${(p) => p.theme.borders.light};
  padding-bottom: 20px;
  margin-bottom: 20px;
`;
const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 20px 0 0 0 !important;
    display: flex;
    flex-direction: column;
  }
`;
const TabsContentWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  height: 100%;
`;
const ChartWrapper = styled.div`
  height: 100%;
  padding: 16px 0 8px 0;
`;
const SummaryWrapper = styled.div`
  padding: 16px;
  flex: 0 0 auto;
  border-top: ${(p) => p.theme.borders.light};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding: 16px;
      border-top: ${p.theme.borders.lightest};
    `};
`;
const OrdersWrapper = styled.div`
  padding: 16px 16px 12px;
`;
const SummaryTitle = styled(Text)`
  display: block;
  margin-bottom: 16px;
  font-size: ${(p) => p.theme.typography.fontSizes.L};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-bottom: 12px;
      color: ${p.theme.colors.titleColor};
      font-size: 15px;
      font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
    `};
`;
